import React, { useMemo } from 'react'
import { ToolkitPageLayout } from '../../../components'
import { Link, useLocation } from 'react-router-dom'

export const PainManagementTrainingPage: React.FC = () => {
  const location = useLocation()

  // Determine breadcrumbs based on the referring page
  const breadcrumbs = useMemo(() => {
    const baseCrumbs = [{ label: 'Toolkit', path: '/toolkit' }]

    // Check if coming from providers, clinics, or elsewhere
    if (location.state?.from === 'providers') {
      baseCrumbs.push(
        { label: 'Providers', path: '/toolkit/providers' },
        { label: 'Training Modules', path: '/toolkit/providers/training' }
      )
    } else if (location.state?.from === 'clinics') {
      baseCrumbs.push(
        { label: 'Clinics', path: '/toolkit/clinics' },
        { label: 'Training Modules', path: '/toolkit/clinics/training' }
      )
    }

    baseCrumbs.push({
      label: 'Pain Management Strategies Training',
      path: '/toolkit/shared/pain-management-training'
    })

    return baseCrumbs
  }, [location.state])

  return (
    <ToolkitPageLayout
      title="Pain Management Strategies Training"
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-8">
        {/* Quote Section */}
        <div className="bg-gray-50 p-6 border-l-4 border-[#A6410A]">
          <blockquote className="text-lg italic text-gray-700">
            The goal for you is to understand pathophysiology of pain and
            various pain management strategies when managing acute and chronic
            pain in sickle cell disease.
          </blockquote>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Step 1: Presentation and Video */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-4">
              1. Listen to and review the following presentation on{' '}
              <em>
                Creating an Institution Wide Approach to Pain Management
                Strategies
              </em>{' '}
              and review the following documents linked{' '}
              <a
                href="https://docs.google.com/presentation/d/1S2VgvdOCazdcAf8AtZrvuYLshjG5yOg7xrhaGokQZDc/edit?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#A6410A] hover:text-[#894d35] underline"
              >
                here
              </a>
              .
            </p>

            {/* Video Section */}
            <div className="mt-6">
              <p className="text-base text-gray-700 mb-3">
                Watch: Pain Management in Sickle Cell Disease by Dr. David
                Carroll
              </p>
              <div className="aspect-w-16 aspect-h-9">
                <video
                  controls
                  className="w-full h-96 rounded-lg bg-gray-100"
                  preload="metadata"
                >
                  <source
                    src="https://www.dropbox.com/scl/fi/qe8ib3mrxoci7ffma14at/JH-Carroll-David-Pain-in-SCD-1.mp4?rlkey=of75ufs3vr7zjzb6m5gjc0jlg&dl=0&dl=1"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: ASH Guidelines */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-4">
              2. Read the{' '}
              <em>
                American Society of Hematology (ASH) Guidelines for Sickle Cell
                Disease:
              </em>{' '}
              Management of Acute and Chronic Pain.
            </p>
            <div className="mt-4">
              <a
                href="https://www.ariseinitiative.org/wp-content/uploads/2020/07/ASH-guidelines_management-of-acute-and-chronic-pain_2020.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#A6410A] text-white rounded hover:bg-[#894d35] transition-colors"
              >
                Read ASH Guidelines
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Step 3: Video */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-4">
              3. Listen to the{' '}
              <em>
                American Society of Hematology (ASH) Clinical Practice
                Guidelines on Sickle Cell Disease: Pain Management.
              </em>
            </p>
            {/* YouTube Video Embed */}
            <div className="aspect-w-16 aspect-h-9 mb-4">
              <iframe
                src="https://www.youtube.com/embed/U4uqJTr0pNU"
                title="ASH Guidelines on SCD: Pain Management"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-96 rounded-lg"
              />
            </div>
          </div>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Completion Link */}
        <div className="text-center py-8">
          <Link
            to={
              location.state?.from === 'providers'
                ? '/toolkit/providers/training'
                : location.state?.from === 'clinics'
                  ? '/toolkit/clinics/training'
                  : '/toolkit'
            }
            className="inline-block px-8 py-4 bg-[#A6410A] text-white font-semibold rounded hover:bg-[#894d35] transition-colors shadow-md hover:shadow-lg"
          >
            {location.state?.from === 'providers'
              ? 'ALL DONE? CLICK HERE TO RETURN TO THE PROVIDERS SCREEN'
              : location.state?.from === 'clinics'
                ? 'ALL DONE? CLICK HERE TO RETURN TO THE CLINICS SCREEN'
                : 'ALL DONE? CLICK HERE TO RETURN TO THE MAIN SCREEN'}
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
